import { CheckCircle2, XCircle, AlertTriangle, Info, Server } from "lucide-react";
import type {
  PageDomainHealthScan,
  PageDomainHealthIssue,
  PageDomainHealthIssueSeverity,
  PageDomainHealthProviderSignal,
  PageDomainHealthRecord,
} from "@/hooks/use-page-domains";

// ---------------------------------------------------------------------------
// DOM-G: rich DNS-scanner breakdown for /settings/domains. PR #721 wired
// scanner.scan() through /crm/pages/domains/:id/health. DOM-B exposed a flat
// "A yes · MX yes" summary. This panel turns the raw DnsScanResult into a
// structured surface — record-type badges, record values, grouped provider
// signals, and severity-grouped issue list — without inflating domains.tsx.
// ---------------------------------------------------------------------------

const RECORD_TYPES: readonly { readonly key: RecordKey; readonly label: string }[] = [
  { key: "aRecords", label: "A" },
  { key: "mxRecords", label: "MX" },
  { key: "nameservers", label: "NS" },
  { key: "caaRecords", label: "CAA" },
] as const;

type RecordKey = "aRecords" | "mxRecords" | "nameservers" | "caaRecords";

const SEVERITY_ORDER: readonly PageDomainHealthIssueSeverity[] = [
  "critical",
  "warning",
  "info",
] as const;

const SEVERITY_LABEL: Record<PageDomainHealthIssueSeverity, string> = {
  critical: "Critical",
  warning: "Warning",
  info: "Info",
};

function badgeClassesForFound(found: boolean): string {
  return found
    ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30"
    : "bg-red-500/10 text-red-700 border-red-500/30";
}

function severityRowClasses(severity: PageDomainHealthIssueSeverity): string {
  if (severity === "critical") return "text-red-700";
  if (severity === "warning") return "text-amber-700";
  return "text-muted-foreground";
}

function severityIcon(severity: PageDomainHealthIssueSeverity) {
  if (severity === "critical") return <AlertTriangle className="h-3 w-3" aria-hidden="true" />;
  if (severity === "warning") return <AlertTriangle className="h-3 w-3" aria-hidden="true" />;
  return <Info className="h-3 w-3" aria-hidden="true" />;
}

function formatRecordValue(record: PageDomainHealthRecord): string {
  if (record.priority !== undefined) {
    return `${record.priority} ${record.value}`;
  }
  return record.value;
}

function groupIssuesBySeverity(
  issues: readonly PageDomainHealthIssue[],
): Record<PageDomainHealthIssueSeverity, PageDomainHealthIssue[]> {
  const grouped: Record<PageDomainHealthIssueSeverity, PageDomainHealthIssue[]> = {
    critical: [],
    warning: [],
    info: [],
  };
  for (const issue of issues) {
    grouped[issue.severity].push(issue);
  }
  return grouped;
}

function groupProvidersBySource(
  signals: readonly PageDomainHealthProviderSignal[],
): Map<string, PageDomainHealthProviderSignal[]> {
  const map = new Map<string, PageDomainHealthProviderSignal[]>();
  for (const signal of signals) {
    const list = map.get(signal.provider) ?? [];
    list.push(signal);
    map.set(signal.provider, list);
  }
  return map;
}

interface RecordTypeRowProps {
  readonly label: string;
  readonly found: boolean;
  readonly records: readonly PageDomainHealthRecord[];
}

function RecordTypeRow({ label, found, records }: RecordTypeRowProps) {
  return (
    <div
      data-testid={`dns-record-row-${label}`}
      className="flex items-start gap-3 rounded-md border border-border bg-background p-2.5"
    >
      <span
        className={`mt-0.5 inline-flex h-5 min-w-10 items-center justify-center rounded border px-2 text-[10px] font-semibold uppercase tracking-wide ${badgeClassesForFound(
          found,
        )}`}
      >
        {label}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-xs">
          {found ? (
            <CheckCircle2 className="h-3 w-3 text-emerald-600" aria-hidden="true" />
          ) : (
            <XCircle className="h-3 w-3 text-red-600" aria-hidden="true" />
          )}
          <span className="font-medium text-foreground">
            {found ? `${records.length} record${records.length === 1 ? "" : "s"} found` : "Missing"}
          </span>
        </div>
        {found && records.length > 0 ? (
          <ul className="mt-1 space-y-0.5 font-mono text-[11px] text-muted-foreground">
            {records.slice(0, 4).map((record, index) => (
              <li key={`${label}-${index}-${record.value}`} className="truncate">
                {formatRecordValue(record)}
              </li>
            ))}
            {records.length > 4 ? (
              <li className="text-[11px] italic text-muted-foreground/70">
                +{records.length - 4} more
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

interface ProviderSignalsBlockProps {
  readonly signals: readonly PageDomainHealthProviderSignal[];
}

function ProviderSignalsBlock({ signals }: ProviderSignalsBlockProps) {
  if (signals.length === 0) return null;
  const grouped = groupProvidersBySource(signals);

  return (
    <div data-testid="dns-provider-signals" className="space-y-1.5">
      <p className="flex items-center gap-1.5 text-xs font-medium text-foreground">
        <Server className="h-3 w-3" aria-hidden="true" />
        Provider signals
      </p>
      <ul className="flex flex-wrap gap-1.5">
        {Array.from(grouped.entries()).map(([provider, providerSignals]) => {
          const sources = Array.from(new Set(providerSignals.map((p) => p.source))).join(", ");
          return (
            <li
              key={provider}
              data-testid={`dns-provider-${provider}`}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] text-foreground"
            >
              <span className="font-medium">{provider}</span>
              <span className="text-muted-foreground">({sources})</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface IssuesBlockProps {
  readonly issues: readonly PageDomainHealthIssue[];
}

function IssuesBlock({ issues }: IssuesBlockProps) {
  if (issues.length === 0) return null;
  const grouped = groupIssuesBySeverity(issues);

  return (
    <div data-testid="dns-issues" className="space-y-2">
      <p className="text-xs font-medium text-foreground">Issues</p>
      {SEVERITY_ORDER.map((severity) => {
        const list = grouped[severity];
        if (list.length === 0) return null;
        return (
          <div key={severity} data-testid={`dns-issues-${severity}`} className="space-y-0.5">
            <p className={`text-[11px] font-semibold uppercase tracking-wide ${severityRowClasses(severity)}`}>
              {SEVERITY_LABEL[severity]} ({list.length})
            </p>
            <ul className="ml-1 space-y-0.5">
              {list.map((issue, index) => (
                <li
                  // Multiple issues can share code+severity (e.g. DKIM_ISSUE
                  // for different selectors). Include message + index so React
                  // reconciliation cannot reuse the wrong row across scans.
                  key={`${issue.code}-${issue.severity}-${index}-${issue.message ?? ""}`}
                  className={`flex items-start gap-1.5 text-[11px] ${severityRowClasses(severity)}`}
                >
                  {severityIcon(severity)}
                  <span>
                    <span className="font-mono font-semibold">{issue.code}</span>
                    {issue.message ? `: ${issue.message}` : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

export interface DomainHealthPanelProps {
  readonly scan: PageDomainHealthScan;
}

export function DomainHealthPanel({ scan }: DomainHealthPanelProps) {
  return (
    <div
      data-testid="dns-health-panel"
      className="mt-3 space-y-3 rounded-md border border-border bg-muted/30 p-3"
    >
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {RECORD_TYPES.map(({ key, label }) => {
          const result = scan[key];
          return (
            <RecordTypeRow
              key={label}
              label={label}
              found={result.found}
              records={result.records}
            />
          );
        })}
      </div>
      <ProviderSignalsBlock signals={scan.providerSignals} />
      <IssuesBlock issues={scan.issues} />
    </div>
  );
}
